import React, { useState, useEffect } from "react";
import { format } from "date-fns";
import {
  Calendar as CalendarIcon,
  Plus,
  Pencil,
  Trash2,
  Building2,
  CreditCard,
  X,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import AddBankAccountModal from "../../modals/AddBankAccountModal";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { AddBankAccount } from "../../API  Functions/ProfileFunctions";
import { useSelector } from "react-redux";

const BankDetails = ({ data, onSubmit }) => {
  const { t, i18n } = useTranslation();
  const [accounts, setAccounts] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [formData, setFormData] = useState({
    accountNumber: "",
    accountName: "",
    bankName: "",
    branchName: "",
    swiftCode: "",
    iban: "",
    balanceDate: new Date(),
    balance: "",
    isActive: false,
  });
  const companyId = useSelector(
    (state) => state?.login?.user?.result?.company_id,
  );

  useEffect(() => {
    if (data && data.length) {
      const formattedData = data.map((account) => ({
        id: account.account_id,
        accountNumber: account.account_number,
        accountName: account.account_name,
        bankId: account.bank_id || null,
        bankName: account.bank_name,
        branchName: account.branch_name,
        swiftCode: account.swift_code,
        iban: account.iban,
        balanceDate: account?.balance_as_on
          ? new Date(account.balance_as_on)
          : null,
        // balanceDate: new Date(account?.balance_as_on),
        balance: account.balance,
        isActive: account.is_active,
      }));
      setAccounts(formattedData);
    }
  }, [data]);

  const resetForm = React.useCallback(() => {
    setFormData({
      accountNumber: "",
      accountName: "",
      bankName: "",
      branchName: "",
      swiftCode: "",
      iban: "",
      balanceDate: null,
      balance: "",
      isActive: false,
    });
    setEditingAccount(null);
  }, [setFormData]);

  const handleAddAccount = () => {
    if (accounts.length >= 3) {
      toast.error(t("Maximum 3 bank accounts allowed"));
      return;
    }
    resetForm();
    setIsDialogOpen(true);
  };

  const handleEditAccount = (account) => {
    setEditingAccount(account);
    console.log("EDIRRR", account);

    setFormData({
      accountNumber: account.accountNumber,
      accountName: account.accountName,
      bankId: account.bankId || account.bank_id || "",
      bankName: account?.bankName,
      branchName: account.branchName,
      swiftCode: account.swiftCode,
      iban: account.iban,
      balanceDate: account?.balanceDate,
      balance: account?.balance.toString(),
      isActive: account.isActive,
    });
    setIsDialogOpen(true);
  };

  // const handleDeleteAccount = (id) => {
  //     setAccounts(accounts.filter((acc) => acc.id !== id));
  //     toast.success(t("Bank account deleted successfully"));
  // };

  const handleDeleteAccount = async (id) => {
    // e.preventDefault();

    // setLoading(true);

    const payload = {
      company_id: companyId,
      account_id: id,
      is_delete: true,
    };

    try {
      let response;

      response = await AddBankAccount(payload);

      const status = response?.data?.result?.status;

      if (status === 200) {
        toast.success(t("Bank account deleted successfully."));

        if (typeof onSubmit === "function") {
          onSubmit(); // Notify parent to refresh
        }
      } else {
        toast.error(response?.data?.result?.message || "Something went wrong.");
      }
    } catch (error) {
      console.error("API error:", error);
      toast.error(error.message || "An error occurred.");
    } finally {
      // setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (
      !formData.accountNumber ||
      !formData.accountName ||
      !formData.bankName ||
      !formData.branchName ||
      !formData.swiftCode ||
      !formData.iban ||
      !formData.balance
    ) {
      toast.error(t("Please fill in all required fields"));
      return;
    }

    if (editingAccount) {
      setAccounts(
        accounts.map((acc) =>
          acc.id === editingAccount.id
            ? {
                ...acc,
                ...formData,
                balance: parseFloat(formData.balance),
              }
            : formData.isActive
              ? { ...acc, isActive: false }
              : acc,
        ),
      );
      toast.success(t("Bank account updated successfully"));
    } else {
      const newAccount = {
        id: Date.now().toString(),
        ...formData,
        balance: parseFloat(formData?.balance),
        isActive: accounts.length === 0 ? true : formData.isActive,
      };

      const updatedAccounts = formData?.isActive
        ? accounts.map((acc) => ({ ...acc, isActive: false }))
        : accounts;

      setAccounts([...updatedAccounts, newAccount]);
      toast.success(t("Bank account added successfully"));
    }

    setIsDialogOpen(false);
    resetForm();
  };

  return (
    <div className="bg-background md:p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-lg font-normal text-foreground">
              {t("Bank Account Details")}
            </h1>
            <p className="text-muted-foreground text-sm">
              {t("Manage your bank accounts (Maximum 3 accounts)")}
            </p>
          </div>
          <button
            onClick={handleAddAccount}
            disabled={accounts.length >= 3}
            className="flex items-center text-sm md:text-md gap-2 bg-primary hover:bg-primary/90 text-primary-foreground px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus className="h-4 w-4" />
            {t("Add Bank Account")}
          </button>
        </div>

        {accounts.length === 0 ? (
          <div className="border-2 border-dashed border-border rounded-lg p-12 text-center">
            <Building2 className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-base font-normal mb-2 text-foreground">
              {t("No Bank Accounts Added")}
            </h3>
            <p className="text-muted-foreground text-sm mb-6">
              {t("Add your bank account details to get started")}
            </p>
            {/* <button
                            onClick={handleAddAccount}
                            className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-primary-foreground px-4 py-2 rounded-lg font-medium transition-colors"
                        >
                            <Plus className="h-4 w-4" />
                            Add Your First Account
                        </button> */}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {accounts?.map((account) => (
              <div
                key={account?.id}
                className="bg-card border border-border rounded-lg p-4 md:p-6 hover:shadow-lg transition-shadow border-l-4 border-l-primary"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <CreditCard className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3
                        className="font-semibold text-lg text-card-foreground max-w-[120px] break-words "
                        title={account?.accountName}
                      >
                        {account?.accountName}
                      </h3>
                      <p
                        className="text-sm text-muted-foreground max-w-[250px] break-words"
                        title={account?.bankName}
                      >
                        {account?.bankName}
                      </p>
                    </div>
                  </div>

                  {account?.isActive && (
                    <span className="ms-2 px-2 py-0.5 text-xs font-semibold text-primary bg-green-200 rounded-full">
                      {t("Primary")}
                    </span>
                  )}
                </div>

                <div className="space-y-4 mb-6">
                  <div>
                    <p className="text-xs text-muted-foreground ">
                      {t("Account Number")}
                    </p>
                    <p className="font-mono text-sm text-card-foreground">
                      {account?.accountNumber}
                    </p>
                  </div>

                  <div>
                    <p className="text-xs text-muted-foreground ">
                      {t("Branch")}
                    </p>
                    <p className="text-sm text-card-foreground">
                      {account?.branchName}
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-xs text-muted-foreground ">
                        {t("Swift Code")}
                      </p>
                      <p className="font-mono text-sm text-card-foreground">
                        {account?.swiftCode}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground ">
                        {t("Balance")}
                      </p>
                      <p className="font-semibold text-success">
                        EGP {account?.balance.toLocaleString()}
                      </p>
                    </div>
                  </div>

                  <div>
                    <p className="text-xs text-muted-foreground ">{"IBAN"}</p>
                    <p className="font-mono text-xs text-card-foreground break-all">
                      {account?.iban}
                    </p>
                  </div>

                  <div>
                    <p className="text-xs text-muted-foreground ">
                      {t("Balance as on")}
                    </p>
                    <p className="text-sm text-card-foreground">
                      {account?.balanceDate
                        ? account.balanceDate instanceof Date
                          ? account.balanceDate.toISOString().split("T")[0] // gives "YYYY-MM-DD"
                          : account.balanceDate // if it's already a string
                        : t("N/A")}
                    </p>
                  </div>

                  {/* <div>
                                        <p className="text-xs text-muted-foreground ">{t("Balance as on")}</p>
                                        <p className="text-sm text-card-foreground">
                                            {account?.balanceDate && !isNaN(new Date(account.balanceDate))
                                                ? format(new Date(account.balanceDate), "dd MMM yyyy")
                                                : t("N/A")}
                                        </p>
                                    </div> */}
                </div>

                <div className="flex gap-2 pt-4 border-t border-border">
                  <button
                    onClick={() => handleEditAccount(account)}
                    className="flex-1 flex items-center justify-center gap-2 border border-input bg-background hover:bg-primaryBlue hover:text-accent-foreground px-3 py-2 rounded-md text-sm font-medium transition-colors"
                  >
                    <Pencil className="w-4 h-4" />
                    {t("Edit")}
                  </button>
                  {/* {!account.isActive && (
                                        <button
                                            onClick={() => handleDeleteAccount(account.id)}
                                            className="flex-1 flex items-center justify-center gap-2 bg-destructive hover:bg-destructive/90 text-destructive-foreground px-3 py-2 rounded-md text-sm font-medium transition-colors"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                            {t("Delete")}
                                        </button>
                                    )} */}
                </div>
              </div>
            ))}
          </div>
        )}

        {accounts.length > 0 && accounts.length < 3 && (
          <div className="mt-6 text-center">
            <p className="text-sm text-muted-foreground mb-3">
              {/* {t("bankAccounts.addMoreAccounts", { count: 3 - accounts.length })} */}
              {t("You can add")} {3 - accounts.length} {t("more account")}
              {/* {3 - accounts.length > 1 ? "s" : ""} */}
            </p>
          </div>
        )}
      </div>

      <AddBankAccountModal
        isOpen={isDialogOpen}
        editingAccount={editingAccount}
        setIsDialogOpen={setIsDialogOpen}
        resetForm={resetForm}
        handleSubmit={handleSubmit}
        formData={formData} // Pass controlled formData
        setFormData={setFormData}
        onSubmit={onSubmit}
        isPrimary={formData.isActive}
        setIsPrimary={(value) => setFormData({ ...formData, isActive: value })}
        accounts={accounts} // Pass this to know if it’s the first account
      />
    </div>
  );
};

export default BankDetails;
